import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsElement from '../../core/ids-element';

import styles from './ids-badge.scss';

const Base = IdsEventsMixin(
  IdsElement
);

export type IdsBadgeShape = 'normal' | 'round';

/**
 * IDS Badge Component
 * @type {IdsBadge}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @part badge - the badge element
 */
@customElement('ids-badge')
@scss(styles)
export default class IdsBadge extends Base {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.color) this.color = this.getAttribute('color');
    this.#updateAriaLabel();
  }

  /**
   * Runs after the component is rendered
   */
  onAfterRender() {
    this.#updateAriaLabel();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attribute in an array
   */
  static get attributes(): string[] {
    return [
      attributes.COLOR,
      attributes.DISABLED,
      attributes.NO_MARGINS,
      attributes.SHAPE,
      htmlAttributes.ARIA_LABEL
    ];
  }

  /**
   * Create the Template for the contents
   * @returns {string} The Template
   */
  template() {
    return `<span class="ids-badge ${this.shape}" part="badge"><slot></slot></span>`;
  }

  /**
   * Updates the aria-label based on the badge content
   * @private
   */
  #updateAriaLabel() {
    if (!this.container) return;

    let labelText = '';

    if (this.ariaLabel) {
      // Use custom aria-label if provided
      labelText = this.ariaLabel;
    } else {
      // Auto-generate aria-label based on content
      const textContent = this.textContent?.trim() || '';
      labelText = textContent;
    }

    // Add disabled state to the label for screen readers
    if (this.disabled && labelText) {
      labelText = `${labelText}, disabled`;
    }

    if (labelText) {
      this.container.setAttribute(htmlAttributes.ARIA_LABEL, labelText);
    } else {
      this.container.removeAttribute(htmlAttributes.ARIA_LABEL);
    }

    // Set aria-disabled attribute based on disabled state
    this.container.toggleAttribute(htmlAttributes.ARIA_DISABLED, !!this.disabled);
  }

  /**
   * Return the badge shape between normal (default) and round
   * @returns {IdsBadgeShape} The path data
   */
  get shape(): IdsBadgeShape {
    return this.getAttribute('shape') as IdsBadgeShape || 'normal';
  }

  /**
   * Set the badge shape between normal (default) and round
   * @param {IdsBadgeShape} value The Badge Shape
   */
  set shape(value: IdsBadgeShape) {
    if (value) {
      this.setAttribute('shape', value.toString());
    } else {
      this.removeAttribute('shape');
    }
    this.container?.classList.remove('normal', 'round');
    this.container?.classList.add(value);
  }

  /**
   * Return the badge color
   * @returns {string | null} the path data
   */
  get color(): string | null {
    return this.getAttribute('color');
  }

  /**
   * Set the color
   * @param {string | null} value The Badge Color [base, error, info, success and warning]
   */
  set color(value: string | null) {
    if (value) {
      this.setAttribute('color', value);
      this.container?.setAttribute('color', value);
      if (value === 'error' || value === 'info' || value === 'warning') {
        this.container?.classList.add('ids-white');
      }
    } else {
      this.removeAttribute('color');
      if (this.container) {
        this.container?.removeAttribute('color');
        this.container.style.backgroundColor = '';
        this.container.style.borderColor = '';
        this.container.style.color = '';
        this.container.style.position = '';
      }
    }
  }

  /**
   * Sets the disabled state
   * @param {boolean | string} value The value
   */
  set disabled(value: boolean | string) {
    if (stringToBool(value)) this.setAttribute(attributes.DISABLED, '');
    else this.removeAttribute(attributes.DISABLED);
    this.#updateAriaLabel();
  }

  get disabled(): boolean | string {
    return this.hasAttribute(attributes.DISABLED);
  }

  /**
   * Sets whether the badge should have no margins
   * @param {boolean | string} val True to remove margins, false to keep default margins
   */
  set noMargins(val: boolean | string) {
    const bool = stringToBool(val);
    this.toggleAttribute(attributes.NO_MARGINS, bool);
    this.container?.classList.toggle(attributes.NO_MARGINS, bool);
  }

  /**
   * Gets whether the badge has no margins
   * @returns {boolean | string} True if margins are removed, false otherwise
   */
  get noMargins(): boolean | string {
    return stringToBool(this.getAttribute(attributes.NO_MARGINS));
  }

  /**
   * Set the aria-label for accessibility
   * @param {string | null} value The aria-label text
   */
  set ariaLabel(value: string | null) {
    if (value) {
      this.setAttribute(htmlAttributes.ARIA_LABEL, value);
    } else {
      this.removeAttribute(htmlAttributes.ARIA_LABEL);
    }
    this.#updateAriaLabel();
  }

  /**
   * Get the aria-label
   * @returns {string | null} The aria-label text
   */
  get ariaLabel(): string | null {
    return this.getAttribute(htmlAttributes.ARIA_LABEL);
  }
}
